import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { cn } from "@/lib/utils";
import { Reveal } from "@/components/motion/Reveal";
import { SectionIntro } from "@/components/site/Bits";
import { AutomationVisual, CareVisual, WebsiteVisual } from "@/components/home/ServiceVisuals";

const ROWS = [
  {
    id: "websites",
    label: "01 — Websites",
    title: "A website that works as hard as you do.",
    copy: "Custom-designed, phone-friendly sites that show what you do, where you work, and how to reach you.",
    points: ["Designed around your free mockup", "Tap-to-call, maps, and quote forms", "Set up to be found on Google"],
    Visual: WebsiteVisual,
  },
  {
    id: "care-plans",
    label: "02 — Care Plans",
    title: "We keep it running, so you don't have to.",
    copy: "Monthly care covers updates, backups, and the small edits that come up, like new hours, photos, or services.",
    points: ["Updates and backups handled", "Quick edits when you need them", "A real person to call or email"],
    Visual: CareVisual,
  },
  {
    id: "automation",
    label: "03 — Automation",
    title: "Stop losing jobs to missed calls.",
    copy: "Simple add-ons that text back missed callers, ask happy customers for reviews, and let people book online.",
    points: ["Missed-call text-back", "Review requests", "Online booking"],
    Visual: AutomationVisual,
  },
];

export const ServicesOverview = () => (
  <section className="section border-t border-white/[0.08]" data-testid="services-overview-section">
    <div className="container-x">
      <SectionIntro
        eyebrow="What we do"
        title={["Everything you need online.", "Nothing you don't."]}
        copy="Websites, ongoing care, and a few smart automations. Pick what fits your business today and add more later."
        testId="services-overview-heading"
      />
      <div className="mt-12">
        {ROWS.map(({ id, label, title, copy, points, Visual }, i) => (
          <div
            key={id}
            className="grid items-center gap-10 border-t border-white/[0.08] py-14 first:border-t-0 lg:grid-cols-12 lg:gap-16 lg:py-20"
            data-testid={`service-row-${id}`}
          >
            <Reveal className={cn("lg:col-span-5", i % 2 === 1 && "lg:order-2")}>
              <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#2DD4D4]">{label}</p>
              <h3 className="mt-4 text-2xl font-semibold leading-tight tracking-[-0.03em] sm:text-3xl lg:text-4xl">{title}</h3>
              <p className="mt-4 text-base leading-relaxed text-[#A1A1AA]">{copy}</p>
              <ul className="mt-6 space-y-2.5">
                {points.map((p) => (
                  <li key={p} className="flex items-center gap-3 text-[15px] text-white/90">
                    <Check className="h-4 w-4 shrink-0 text-[#2F6BFF]" aria-hidden="true" />
                    {p}
                  </li>
                ))}
              </ul>
              <Link
                to={`/services#${id}`}
                className="group mt-8 inline-flex items-center gap-2 text-sm font-medium text-white"
                data-testid={`service-row-link-${id}`}
              >
                See packages
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" aria-hidden="true" />
              </Link>
            </Reveal>
            <Reveal delay={0.1} className={cn("lg:col-span-7", i % 2 === 1 && "lg:order-1")}>
              <div className="relative overflow-hidden rounded-2xl border border-white/[0.08] bg-[#0C0E12] p-3 sm:p-6">
                <div
                  aria-hidden="true"
                  className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full bg-[radial-gradient(closest-side,rgba(47,107,255,0.28),transparent)]"
                />
                <div className="relative min-h-[300px] sm:min-h-[360px]">
                  <Visual />
                </div>
              </div>
            </Reveal>
          </div>
        ))}
      </div>
    </div>
  </section>
);
